/**
 * LocalStorage Utility Functions
 *
 * Helper functions for managing localStorage data
 */

import type { AuthTokens } from '@/types';
import { cognitoConfig } from '@/config';

/**
 * LocalStorage keys
 */
export const STORAGE_KEYS = {
  REDIRECT_URI: 'redirectUri',
  AUTH_STATE: 'authState',
  LAST_EMAIL: 'lastEmail',
} as const;

/**
 * Save redirect URI to localStorage
 * @param uri - Redirect URI to save
 */
export function saveRedirectUri(uri: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.REDIRECT_URI, uri);
  } catch (e) {
    console.error('Failed to save redirect URI:', e);
  }
}

/**
 * Get redirect URI from localStorage
 * @returns Redirect URI or null
 */
export function getRedirectUri(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.REDIRECT_URI);
  } catch (e) {
    console.error('Failed to get redirect URI:', e);
    return null;
  }
}

/**
 * Clear redirect URI from localStorage
 */
export function clearRedirectUri(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.REDIRECT_URI);
  } catch (e) {
    console.error('Failed to clear redirect URI:', e);
  }
}

/**
 * Get Cognito tokens from localStorage
 * Cognito SDK stores tokens in localStorage with specific key patterns
 * @returns Auth tokens or null if not found
 */
export function getCognitoTokens(): AuthTokens | null {
  try {
    // Cognito stores tokens with keys like:
    // CognitoIdentityServiceProvider.{clientId}.{userId}.idToken
    // CognitoIdentityServiceProvider.{clientId}.{userId}.accessToken
    // CognitoIdentityServiceProvider.{clientId}.{userId}.refreshToken

    const prefix = `CognitoIdentityServiceProvider.${cognitoConfig.userPoolClientId}`;

    // Find all keys that match the pattern
    const keys = Object.keys(localStorage).filter(key => key.startsWith(prefix));

    // Extract user ID from keys
    const userIdKey = keys.find(key => key.endsWith('.LastAuthUser'));
    if (!userIdKey) {
      return null;
    }

    const userId = localStorage.getItem(userIdKey);
    if (!userId) {
      return null;
    }

    // Build token keys
    const idTokenKey = `${prefix}.${userId}.idToken`;
    const accessTokenKey = `${prefix}.${userId}.accessToken`;
    const refreshTokenKey = `${prefix}.${userId}.refreshToken`;

    // Get tokens
    const idToken = localStorage.getItem(idTokenKey);
    const accessToken = localStorage.getItem(accessTokenKey);
    const refreshToken = localStorage.getItem(refreshTokenKey);

    if (!idToken || !accessToken || !refreshToken) {
      return null;
    }

    return {
      idToken,
      accessToken,
      refreshToken,
    };
  } catch (e) {
    console.error('Failed to get Cognito tokens:', e);
    return null;
  }
}

/**
 * Clear all Cognito tokens from localStorage
 */
export function clearCognitoTokens(): void {
  try {
    const prefix = `CognitoIdentityServiceProvider.${cognitoConfig.userPoolClientId}`;
    const keys = Object.keys(localStorage).filter(key => key.startsWith(prefix));

    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (e) {
    console.error('Failed to clear Cognito tokens:', e);
  }
}

/**
 * Save last used email
 * @param email - Email to save
 */
export function saveLastEmail(email: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_EMAIL, email);
  } catch (e) {
    console.error('Failed to save last email:', e);
  }
}

/**
 * Get last used email
 * @returns Last email or null
 */
export function getLastEmail(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.LAST_EMAIL);
  } catch (e) {
    console.error('Failed to get last email:', e);
    return null;
  }
}

/**
 * Clear last email
 */
export function clearLastEmail(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.LAST_EMAIL);
  } catch (e) {
    console.error('Failed to clear last email:', e);
  }
}

/**
 * Clear all auth-related data from localStorage
 */
export function clearAllAuthData(): void {
  clearRedirectUri();
  clearCognitoTokens();
  clearLastEmail();
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
  } catch (e) {
    console.error('Failed to clear auth state:', e);
  }
}

/**
 * Check if localStorage is available
 * @returns true if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Safe JSON parse from localStorage
 * @param key - LocalStorage key
 * @returns Parsed object or null
 */
export function getJSON<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }
    return JSON.parse(item) as T;
  } catch (e) {
    console.error(`Failed to parse JSON from localStorage key "${key}":`, e);
    return null;
  }
}

/**
 * Safe JSON stringify to localStorage
 * @param key - LocalStorage key
 * @param value - Value to stringify and save
 */
export function setJSON<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Failed to save JSON to localStorage key "${key}":`, e);
  }
}